/**
 * Shared (platform-agnostic) logic for agent artifacts: parsing the tool
 * results the chat stream carries, deciding what a card or the viewer can
 * show, and the refresh and signed-URL policies. Rendering stays in the
 * platform files; everything here is data, so web and native cannot drift.
 */
import type {
  AgentArtifactKind,
  AgentArtifactStatus,
  AgentArtifactSummary,
  ArtifactDownloadVariant,
  ArtifactFormat,
  ArtifactPreviewVariant,
} from '@tabbio/types';

export type ArtifactVariants = AgentArtifactSummary['variants'];

/** Props of both viewer twins (`ArtifactViewer.tsx` and `ArtifactViewer.web.tsx`). */
export type ArtifactViewerProps = {
  artifactId: string;
  onClose: () => void;
  /** The kind when the opener already knows it, so the right viewer shows at once. */
  kindHint?: AgentArtifactKind;
  /** While the artifact builds: the view the opener asked for (a page's code). */
  initialView?: 'preview' | 'code';
};

export type ArtifactImageSize = { width: number; height: number };

/** Props of both `GeneratedImage` twins. */
export type GeneratedImageProps = {
  /** A pending model (no id yet) draws the placeholder at the requested ratio. */
  model: ArtifactCardModel;
  /** Grid tiles are fixed width; a lone image spans the response column. */
  tileWidth?: number | null;
};

/** Props of both `ImageLightbox` twins. */
export type ImageLightboxProps = {
  artifactId: string;
  title: string;
  onClose: () => void;
};

/** Props of both `ArtifactPanel` twins (web canvas panel, native full screen). */
export type ArtifactPanelProps = {
  artifactId: string;
  /** Web only: beside the chat, or covering the viewport (phones, dialogs). */
  layout: 'docked' | 'fullscreen';
  /** Web only: the full-width view with the chat thread hidden. */
  expanded?: boolean;
  onToggleExpanded?: () => void;
  onClose: () => void;
  /** "Back to chat" when the panel was opened by the owned link. */
  closeMode?: 'close' | 'backToChat';
  /**
   * The view the opener asked for: a page's code while it builds, or the
   * editor (the suggestions card's Open to review).
   */
  initialView?: 'preview' | 'code' | 'edit';
  /** Web: changes on each building open, so the asked view applies again. */
  viewRequest?: number;
  /** Web: false when the panel opened by itself (auto-open), so focus stays put. */
  takeFocus?: boolean;
  /**
   * Web: the version on screen from the panel store (null is the latest);
   * without it the panel keeps its own.
   */
  versionId?: string | null;
  onVersionChange?: (versionId: string | null) => void;
};

/** Everything a card needs, from a tool result or from the artifacts API. */
export type ArtifactCardModel = {
  artifactId: string;
  kind: AgentArtifactKind;
  title: string;
  status: AgentArtifactStatus;
  variants: ArtifactVariants;
  formats: ArtifactFormat[];
  previewAvailable: boolean;
  failure: { code: string; message: string } | null;
  /** Images: the ratio requested at creation ("16:9"), for the placeholder. */
  aspectRatio: string | null;
  /** Images: the stored pixel size once ready. */
  image: ArtifactImageSize | null;
  /** ISO creation time, when the source carries it. */
  createdAt: string | null;
  /**
   * The head's version number (plan 7.5: cards show "Version N" past 1),
   * when the source carries it.
   */
  versionNumber?: number;
  /**
   * The version a tool result made (an agent edit), when it carries one: an
   * image edit is still running while this is not the head.
   */
  resultVersionId?: string;
  /**
   * A version a tool result saved: an agent edit ("Version 3 saved"), or a
   * page kept with findings the checks still flag. `blocking` counts the
   * findings that keep it from being published (a success all the same).
   */
  saved?: ArtifactSavedVersion;
  /**
   * An agent edit of this file is running: the call has no result yet
   * (`updatePage`, `updateDocument`, `editImage`), so the card says Editing.
   */
  editing?: boolean;
};

export type ArtifactSavedVersion = {
  versionNumber: number;
  /** An agent edit (`updatePage`, `updateDocument`) rather than a new file. */
  edited: boolean;
  /** Findings with `blocksPublishing: true`. */
  blocking: number;
};

/**
 * Every kind a card, the viewer or a route may be asked to show. `page` is a
 * model-authored HTML page the server sanitized: HTML only, no PDF and no
 * dark copy, since the page carries its own design.
 */
export const ARTIFACT_KINDS: readonly AgentArtifactKind[] = [
  'document',
  'slides',
  'page',
  'image',
  'file',
];
const STATUSES: readonly AgentArtifactStatus[] = ['pending', 'ready', 'failed'];
const FORMATS: readonly ArtifactFormat[] = ['html', 'pdf', 'pptx'];

/** How often a pending artifact is refetched when no socket nudge arrives. */
export const ARTIFACT_POLL_MS = 5_000;

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readVariants(value: unknown): ArtifactVariants {
  const source = record(value) ?? {};
  return {
    html: source.html === true,
    pdf: source.pdf === true,
    pptx: source.pptx === true,
    image: source.image === true,
    thumbnail: source.thumbnail === true,
  };
}

function readFailure(value: unknown): ArtifactCardModel['failure'] {
  const source = record(value);
  if (!source) return null;
  const code = typeof source.code === 'string' ? source.code : 'RENDER_FAILED';
  const message = typeof source.message === 'string' ? source.message : '';
  return { code, message };
}

function readImageSize(value: unknown): ArtifactImageSize | null {
  const source = record(value);
  if (!source) return null;
  const { width, height } = source;
  return typeof width === 'number' && typeof height === 'number' && width > 0 && height > 0
    ? { width, height }
    : null;
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function readFormats(value: unknown): ArtifactFormat[] {
  return Array.isArray(value)
    ? value.filter((item): item is ArtifactFormat =>
        (FORMATS as readonly unknown[]).includes(item),
      )
    : [];
}

/** Images preview their file; documents, slides and pages their HTML variant. */
function previewFor(kind: AgentArtifactKind, status: AgentArtifactStatus, variants: ArtifactVariants) {
  if (status !== 'ready') return false;
  return kind === 'image' ? variants.image || variants.thumbnail : variants.html;
}

/**
 * `generateImage`, `createDocument`, `createPage` and `artifactGet` return an
 * `ArtifactToolResult`: ids, status and which variants exist, never a URL.
 * Anything else (an error envelope, a half-streamed payload) is not a card.
 */
export function parseArtifactToolResult(result: unknown): ArtifactCardModel | null {
  const source = record(result);
  if (!source) return null;
  const artifactId = typeof source.artifactId === 'string' ? source.artifactId : '';
  if (!artifactId) return null;
  const kind = (ARTIFACT_KINDS as readonly unknown[]).includes(source.kind)
    ? (source.kind as AgentArtifactKind)
    : 'file';
  const status = (STATUSES as readonly unknown[]).includes(source.status)
    ? (source.status as AgentArtifactStatus)
    : 'pending';
  const variants = readVariants(source.variants);
  return {
    artifactId,
    kind,
    title: typeof source.title === 'string' ? source.title : '',
    status,
    variants,
    formats: readFormats(source.formats),
    previewAvailable:
      typeof source.previewAvailable === 'boolean'
        ? source.previewAvailable
        : previewFor(kind, status, variants),
    failure: status === 'failed' ? readFailure(source.failure) ?? { code: 'RENDER_FAILED', message: '' } : null,
    aspectRatio: readString(source.aspectRatio),
    image: readImageSize(source.image),
    createdAt: readString(source.createdAt),
    ...(typeof source.versionNumber === 'number' && source.versionNumber > 0
      ? { versionNumber: source.versionNumber }
      : {}),
    ...(typeof source.versionId === 'string' && source.versionId ? { resultVersionId: source.versionId } : {}),
    ...savedVersionOf(source),
  };
}

/**
 * The saved version a result describes, when it is an agent edit (it names
 * how the change was applied) or a page kept with findings (Phase 2 fix:
 * findings are a success, never a failure).
 */
function savedVersionOf(source: Record<string, unknown>): { saved?: ArtifactSavedVersion } {
  const versionNumber = typeof source.versionNumber === 'number' && source.versionNumber > 0 ? source.versionNumber : 0;
  if (!versionNumber) return {};
  const findings = Array.isArray(source.findings) ? source.findings : [];
  const blocking = findings.filter((finding) => record(finding)?.blocksPublishing === true).length;
  const edited = typeof source.applied === 'string' || typeof source.changeSummary === 'string';
  if (!edited && blocking === 0) return {};
  return { saved: { versionNumber, edited, blocking } };
}

/**
 * A tool result that saved a version of a file (an agent edit, a page kept
 * with findings): it is a success even when an older server marked the call
 * failed, so the card and the activity line read it as done.
 */
export function isSavedArtifactResult(result: unknown): boolean {
  const source = record(result);
  if (!source) return false;
  if (source.ok === false || source.error) return false;
  return (
    typeof source.artifactId === 'string' &&
    Boolean(source.artifactId) &&
    typeof source.versionId === 'string' &&
    Boolean(source.versionId) &&
    typeof source.versionNumber === 'number' &&
    source.status !== 'failed'
  );
}

/**
 * The image a `generateImage` call is making, before it has a result: the
 * title and requested ratio from the call's own arguments, so the
 * placeholder has the right shape from the first frame.
 */
export function pendingImageModel(args: unknown): ArtifactCardModel {
  const source = record(args) ?? {};
  return {
    artifactId: '',
    kind: 'image',
    title: readString(source.title) ?? '',
    status: 'pending',
    variants: readVariants(null),
    formats: [],
    previewAvailable: false,
    failure: null,
    aspectRatio: readString(source.aspectRatio),
    image: null,
    createdAt: null,
  };
}

/** The tools whose call becomes an artifact card. */
export const ARTIFACT_CREATING_TOOLS: readonly string[] = [
  'generateImage',
  'createDocument',
  'createPage',
];

export function isArtifactCreatingTool(toolName: string): boolean {
  return ARTIFACT_CREATING_TOOLS.includes(toolName);
}

/** The agent edit tools (plan 6.2); suggest mode changes nothing. */
export const ARTIFACT_EDITING_TOOLS: readonly string[] = ['updatePage', 'updateDocument', 'editImage'];

/** A call that makes a new version of an existing file (not suggest mode). */
export function isArtifactEditCall(toolName: string, args: unknown): boolean {
  if (!ARTIFACT_EDITING_TOOLS.includes(toolName)) return false;
  return record(args)?.mode !== 'suggest';
}

/** The kind of file an edit call works on, from the tool (a deck edits as a document). */
export function artifactEditKind(toolName: string): AgentArtifactKind {
  return toolName === 'updatePage' ? 'page' : toolName === 'editImage' ? 'image' : 'document';
}

/**
 * The card a creating call shows from the moment it is called, before it has
 * a result: the kind and title come from the call's own arguments, so the
 * chat shows "Product design workflow, Preparing…" while the file is made
 * instead of a blank skeleton. An image keeps its ratio placeholder. An
 * agent edit (not suggest mode) shows the file it edits, marked `editing`.
 * Anything else has no pending card.
 */
export function pendingArtifactModel(toolName: string, args: unknown): ArtifactCardModel | null {
  if (toolName === 'generateImage') return pendingImageModel(args);
  if (isArtifactEditCall(toolName, args)) {
    // The file being edited: its id from the call, the rest from its detail.
    const artifactId = readString(record(args)?.artifactId);
    if (!artifactId) return null;
    return {
      artifactId,
      kind: artifactEditKind(toolName),
      title: '',
      status: 'pending',
      variants: readVariants(null),
      formats: [],
      previewAvailable: false,
      failure: null,
      aspectRatio: null,
      image: null,
      createdAt: null,
      editing: true,
    };
  }
  if (toolName !== 'createDocument' && toolName !== 'createPage') return null;
  const source = record(args) ?? {};
  const kind: AgentArtifactKind =
    toolName === 'createPage' ? 'page' : source.kind === 'slides' ? 'slides' : 'document';
  return {
    artifactId: '',
    kind,
    title: readString(source.title) ?? readString(record(source.document)?.title) ?? '',
    status: 'pending',
    variants: readVariants(null),
    formats: [],
    previewAvailable: false,
    failure: null,
    aspectRatio: null,
    image: null,
    createdAt: null,
  };
}

/** The same model from an `AgentArtifactSummary` (artifacts API, run detail). */
export function artifactModelFromSummary(summary: AgentArtifactSummary): ArtifactCardModel {
  return {
    artifactId: summary.id,
    kind: summary.kind,
    title: summary.title,
    status: summary.status,
    variants: readVariants(summary.variants),
    formats: readFormats(summary.formats),
    previewAvailable: previewFor(summary.kind, summary.status, readVariants(summary.variants)),
    failure: summary.status === 'failed' ? summary.error ?? { code: 'RENDER_FAILED', message: '' } : null,
    aspectRatio: readString(summary.aspectRatio),
    image: readImageSize(summary.image),
    createdAt: readString(summary.createdAt),
    ...(summary.currentVersion?.number ? { versionNumber: summary.currentVersion.number } : {}),
  };
}

/** A fresher server copy (socket nudge or poll) wins over the tool result. */
export function mergeArtifactModel(
  model: ArtifactCardModel,
  live: AgentArtifactSummary | null | undefined,
): ArtifactCardModel {
  if (!live || live.id !== model.artifactId) return model;
  const fresh = artifactModelFromSummary(live);
  const versionNumber = fresh.versionNumber ?? model.versionNumber;
  return {
    ...fresh,
    title: fresh.title || model.title,
    aspectRatio: fresh.aspectRatio ?? model.aspectRatio,
    image: fresh.image ?? model.image,
    ...(versionNumber ? { versionNumber } : {}),
    ...(model.resultVersionId ? { resultVersionId: model.resultVersionId } : {}),
    ...(model.saved ? { saved: model.saved } : {}),
    ...(model.editing ? { editing: true } : {}),
  };
}

// ── Generated images ───────────────────────────────────────────────────────

/** "16:9" → 1.777…; anything unreadable → null. */
export function parseAspectRatio(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^(\d+(?:\.\d+)?)\s*[:/x]\s*(\d+(?:\.\d+)?)$/);
  if (!match) return null;
  const width = Number(match[1]);
  const height = Number(match[2]);
  return width > 0 && height > 0 ? width / height : null;
}

/**
 * The width / height ratio to lay a generated image out at, so it is never
 * cropped and its box never jumps: the stored pixel size once known, else the
 * requested ratio, else square (Gemini's placeholder default).
 */
export function imageAspectRatio(model: Pick<ArtifactCardModel, 'image' | 'aspectRatio'> | null): number {
  if (model?.image) return model.image.width / model.image.height;
  return parseAspectRatio(model?.aspectRatio) ?? 1;
}

/**
 * How a response lays out its generated images (Gemini's image grid): one
 * image spans the response column; several wrap in a 490px grid of 240px
 * tiles with a 10px gap.
 */
export const IMAGE_GRID = { maxWidth: 490, itemWidth: 240, gap: 10, singleMaxWidth: 708 } as const;

export function imageGridLayout(count: number): { mode: 'single' | 'grid'; itemWidth: number | null } {
  return count <= 1 ? { mode: 'single', itemWidth: null } : { mode: 'grid', itemWidth: IMAGE_GRID.itemWidth };
}

/** The file an image card shows in the conversation: the full image, never the 512px preview. */
export function imageDisplayVariant(model: ArtifactCardModel): ArtifactPreviewVariant | null {
  if (model.status !== 'ready') return null;
  if (model.variants.image) return 'image';
  if (model.variants.thumbnail) return 'thumbnail';
  return null;
}

/**
 * "Sep 24, 2:15 PM" in the reader's locale, for the canvas card's secondary
 * line. Empty when the time is unknown or unreadable.
 */
export function formatArtifactTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  } catch {
    return '';
  }
}

/** `artifactList` returns `{ artifacts: AgentArtifactSummary[], hasMore }`. */
export function parseArtifactListResult(result: unknown): {
  artifacts: ArtifactCardModel[];
  hasMore: boolean;
} {
  const source = record(result);
  const raw = Array.isArray(source?.artifacts) ? (source!.artifacts as unknown[]) : [];
  const artifacts: ArtifactCardModel[] = [];
  for (const item of raw) {
    const entry = record(item);
    if (!entry || typeof entry.id !== 'string') continue;
    artifacts.push(
      artifactModelFromSummary({
        ...(entry as unknown as AgentArtifactSummary),
        kind: (ARTIFACT_KINDS as readonly unknown[]).includes(entry.kind)
          ? (entry.kind as AgentArtifactKind)
          : 'file',
        status: (STATUSES as readonly unknown[]).includes(entry.status)
          ? (entry.status as AgentArtifactStatus)
          : 'pending',
        title: typeof entry.title === 'string' ? entry.title : '',
        error: readFailure(entry.error),
      }),
    );
  }
  return { artifacts, hasMore: source?.hasMore === true };
}

/**
 * Download buttons, in order: PDF / PPTX for documents and slides, the image
 * for images, the HTML page for documents (a deck's HTML is just its
 * on-screen preview), and for a page its HTML, the only file it has.
 */
export function artifactDownloadOptions(model: ArtifactCardModel): ArtifactDownloadVariant[] {
  if (model.status !== 'ready') return [];
  const { variants } = model;
  switch (model.kind) {
    case 'image':
      return variants.image ? ['image'] : [];
    case 'slides':
      return [
        ...(variants.pptx ? (['pptx'] as const) : []),
        ...(variants.pdf ? (['pdf'] as const) : []),
      ];
    case 'document':
      return [
        ...(variants.pdf ? (['pdf'] as const) : []),
        ...(variants.html ? (['html'] as const) : []),
      ];
    case 'page':
      return variants.html ? ['html'] : [];
    default:
      return (['pdf', 'pptx', 'image', 'html'] as const).filter((variant) => variants[variant]);
  }
}

/** The card's picture: the stored thumbnail, or an image artifact itself. */
export function artifactThumbnailVariant(model: ArtifactCardModel): ArtifactPreviewVariant | null {
  if (model.status !== 'ready') return null;
  if (model.variants.thumbnail) return 'thumbnail';
  if (model.kind === 'image' && model.variants.image) return 'image';
  return null;
}

export type ArtifactPreviewPlan = {
  /** What the viewer shows first; null when nothing can be previewed. */
  primary: ArtifactPreviewVariant | null;
  /** A PDF the platform can show inline, offered as a second view. */
  pdf: boolean;
};

/**
 * What the viewer can show on a platform. Documents, slides and pages open on
 * their HTML page. A document's PDF is a second view where the platform
 * renders PDFs inline (web, iOS); Android has no inline PDF renderer, so it
 * keeps the HTML page on screen and opens the PDF in the system viewer
 * instead. A page has no PDF, so it is the Preview alone.
 */
export function artifactPreviewPlan(
  model: ArtifactCardModel,
  options: { inlinePdf: boolean },
): ArtifactPreviewPlan {
  if (model.status !== 'ready') return { primary: null, pdf: false };
  const { variants } = model;
  if (model.kind === 'image') {
    return {
      primary: variants.image ? 'image' : variants.thumbnail ? 'thumbnail' : null,
      pdf: false,
    };
  }
  if (model.kind === 'page') {
    return { primary: variants.html ? 'html' : null, pdf: false };
  }
  if (model.kind === 'document' || model.kind === 'slides') {
    const pdf = model.kind === 'document' && variants.pdf && options.inlinePdf;
    if (variants.html) return { primary: 'html', pdf };
    if (pdf) return { primary: 'pdf', pdf: false };
    return { primary: null, pdf: false };
  }
  return { primary: null, pdf: false };
}

export function isArtifactSettled(status: AgentArtifactStatus | null | undefined) {
  return status === 'ready' || status === 'failed';
}

/** React Query `refetchInterval`: poll a pending artifact, stop once settled. */
export function artifactRefetchInterval(
  status: AgentArtifactStatus | null | undefined,
): number | false {
  return status === 'pending' ? ARTIFACT_POLL_MS : false;
}

export type ArtifactErrorKind = 'not-found' | 'offline' | 'not-ready' | 'variant-unavailable' | 'other';

/** Reads the envelope code an `ArtifactsApiError` carries. */
export function classifyArtifactError(error: unknown): ArtifactErrorKind {
  const code =
    error && typeof error === 'object' && 'code' in error
      ? String((error as { code: unknown }).code)
      : '';
  if (code === 'ARTIFACT_NOT_FOUND' || code === 'HTTP_404' || code === 'HTTP_403') {
    return 'not-found';
  }
  if (code === 'NETWORK_ERROR') return 'offline';
  if (code === 'ARTIFACT_NOT_READY') return 'not-ready';
  if (code === 'ARTIFACT_VARIANT_UNAVAILABLE') return 'variant-unavailable';
  return 'other';
}

// ── Signed URL lifecycle ───────────────────────────────────────────────────

/**
 * A signed URL expires after 15 minutes, and storage answers an expired one
 * with 403. The first load failure re-mints automatically; a second one is
 * surfaced as `expired` so the viewer can offer Retry instead of looping.
 */
export type SignedUrlState = { generation: number; failures: number };

export type SignedUrlEvent = { type: 'load-error' } | { type: 'retry' };

export const INITIAL_SIGNED_URL_STATE: SignedUrlState = { generation: 0, failures: 0 };

export function signedUrlReducer(state: SignedUrlState, event: SignedUrlEvent): SignedUrlState {
  if (event.type === 'retry') {
    return { generation: state.generation + 1, failures: 0 };
  }
  const failures = state.failures + 1;
  return {
    failures,
    generation: failures === 1 ? state.generation + 1 : state.generation,
  };
}

export function isSignedUrlExpired(state: SignedUrlState) {
  return state.failures >= 2;
}

// ── Viewer state ───────────────────────────────────────────────────────────

export type ArtifactViewerState =
  | 'loading'
  | 'pending'
  | 'failed'
  | 'unsupported'
  | 'offline'
  | 'expired'
  | 'not-found'
  | 'error'
  | 'ready';

/**
 * One answer for what the viewer shows, in priority order: a gone artifact
 * beats everything, then connectivity, then the artifact's own status, then
 * whether this platform can preview it, then the preview link itself.
 */
export function resolveArtifactViewerState(input: {
  artifact: ArtifactCardModel | null;
  loading: boolean;
  error: unknown;
  plan: ArtifactPreviewPlan | null;
  previewExpired: boolean;
  previewError: unknown;
}): ArtifactViewerState {
  const { artifact, loading, error, plan, previewExpired, previewError } = input;
  if (error && !artifact) {
    const kind = classifyArtifactError(error);
    if (kind === 'not-found') return 'not-found';
    if (kind === 'offline') return 'offline';
    return 'error';
  }
  if (!artifact) return loading ? 'loading' : 'error';
  if (artifact.status === 'pending') return 'pending';
  if (artifact.status === 'failed') return 'failed';
  if (!plan?.primary) return 'unsupported';
  if (previewExpired) return 'expired';
  if (previewError) {
    const kind = classifyArtifactError(previewError);
    if (kind === 'not-found') return 'not-found';
    if (kind === 'offline') return 'offline';
    if (kind === 'variant-unavailable') return 'unsupported';
    if (kind === 'not-ready') return 'pending';
    return 'error';
  }
  return 'ready';
}

/**
 * The native preview WebView may load exactly the signed object and nothing
 * else: same origin and path as the minted URL (the query can be re-encoded by
 * the platform), so links inside the document never navigate the viewer.
 */
export function isAllowedArtifactNavigation(requestUrl: string, signedUrl: string): boolean {
  if (requestUrl === signedUrl) return true;
  try {
    const request = new URL(requestUrl);
    const signed = new URL(signedUrl);
    return request.origin === signed.origin && request.pathname === signed.pathname;
  } catch {
    return false;
  }
}

export type ArtifactPreviewTheme = 'light' | 'dark';

/**
 * The preview theme to ask the API for. Only a document's or a deck's HTML
 * page has a dark copy (rendered server side with `data-theme="dark"`, since
 * the sandboxed preview runs no script); PDFs and images are the same file in
 * either theme, and a `page` carries its own design (the API ignores the
 * theme for it), so they never vary and never re-mint when the app theme
 * changes.
 */
export function artifactPreviewTheme(
  variant: ArtifactPreviewVariant | null,
  isDark: boolean,
  kind?: AgentArtifactKind | null,
): ArtifactPreviewTheme {
  if (kind === 'page') return 'light';
  return variant === 'html' && isDark ? 'dark' : 'light';
}

/** The WebView's origin whitelist entry for a signed URL. */
export function artifactOrigin(signedUrl: string): string | null {
  try {
    return new URL(signedUrl).origin;
  } catch {
    return null;
  }
}

const EXTENSIONS: Record<ArtifactDownloadVariant, string> = {
  pdf: 'pdf',
  pptx: 'pptx',
  html: 'html',
  image: 'png',
};

/** A safe local file name: the title's letters and digits plus the variant's extension. */
export function artifactFileName(
  title: string,
  variant: ArtifactDownloadVariant,
  contentType?: string,
): string {
  const printable = Array.from(title.normalize('NFKC'))
    .filter((char) => char.charCodeAt(0) >= 32)
    .join('');
  const base =
    printable
      .replace(/[\\/:*?"<>|]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 80) || 'tabbio-file';
  let extension = EXTENSIONS[variant];
  if (variant === 'image' && contentType) {
    if (contentType.includes('jpeg')) extension = 'jpg';
    else if (contentType.includes('webp')) extension = 'webp';
  }
  return `${base}.${extension}`;
}
